import {
  RadioTransport,
  BleDeviceLike,
  BleGattServerLike,
  BleServiceLike,
  BleCharacteristicLike,
  NavigatorWithBluetooth,
} from './types';

/**
 * Official Meshtastic BLE GATT UUIDs.
 * Source: https://meshtastic.org/docs/development/firmware/protocol/hardware/ble
 */
const MESHTASTIC_SERVICE_UUID = '6ba1b218-15a8-461f-9fa8-5dcae273eafd';
const FROMRADIO_CHAR_UUID = '2c55e69e-4993-11ed-b878-0242ac120002';
const TORADIO_CHAR_UUID = 'f75c76d2-129e-4dad-a1dd-7866124401e7';
const FROMNUM_CHAR_UUID = 'ed9da18c-a800-4f66-a670-aa7547e34453';

/**
 * WebBluetooth GATT transport driver for Meshtastic nodes.
 *
 * Implements the canonical Meshtastic PhoneAPI BLE protocol:
 * - Single-operation serialized GATT execution to eliminate Android GATT_BUSY race conditions.
 * - Multi-pass drain state machine driven by FROMNUM notifications and needsRedrain flag.
 * - 15ms yields between read operations to accommodate radio firmware and native OS BLE timing.
 * - 500ms safety poll to ensure resilience against dropped Android notifications.
 */
export class BleTransport implements RadioTransport {
  readonly name = 'WebBluetooth';

  private device: BleDeviceLike | null = null;
  private lastDevice: BleDeviceLike | null = null;
  private server: BleGattServerLike | null = null;
  private service: BleServiceLike | null = null;
  private fromRadioChar: BleCharacteristicLike | null = null;
  private toRadioChar: BleCharacteristicLike | null = null;
  private fromNumChar: BleCharacteristicLike | null = null;
  private boundDisconnectHandler: (() => void) | null = null;

  private packetHandlers: ((data: Uint8Array) => void)[] = [];
  private errorHandlers: ((error: Error) => void)[] = [];
  private disconnectHandlers: (() => void)[] = [];

  private isConnectedState = false;
  private isDraining = false;
  private needsRedrain = false;
  private pollTimer: ReturnType<typeof setInterval> | null = null;

  /**
   * Promise chain that serializes all WebBluetooth GATT operations.
   *
   * Why: Android's native BluetoothGatt stack throws a GATT_BUSY / GATT operation already in progress
   * error if a read and write are executed simultaneously. Serializing operations ensures thread-safe
   * access across concurrent readValue and writeValue calls.
   */
  private gattQueue: Promise<unknown> = Promise.resolve();

  private enqueueGatt<T>(operation: () => Promise<T>): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      this.gattQueue = this.gattQueue
        .then(async () => {
          try {
            const res = await operation();
            resolve(res);
          } catch (err) {
            reject(err);
          }
        })
        .catch(() => {});
    });
  }

  /**
   * Prompts user for Bluetooth device selection and connects to Meshtastic GATT service.
   *
   * Why: WebBluetooth requires an explicit user gesture to trigger the device picker modal
   * and subsequently negotiates the GATT connection and characteristic subscriptions.
   */
  async connect(): Promise<void> {
    if (!('bluetooth' in navigator)) {
      throw new Error('WebBluetooth API is not supported by this browser. Use Chrome, Edge, Brave or Opera on supported platforms.');
    }

    try {
      console.info('[BleTransport] Requesting Meshtastic BLE device...');
      const nav = navigator as NavigatorWithBluetooth;
      if (!nav.bluetooth) {
        throw new Error('WebBluetooth API is not supported by this browser.');
      }
      this.device = await nav.bluetooth.requestDevice({
        filters: [{ services: [MESHTASTIC_SERVICE_UUID] }],
        optionalServices: [MESHTASTIC_SERVICE_UUID]
      });

      this.lastDevice = this.device;
      if (this.device.id && typeof localStorage !== 'undefined') {
        try {
          localStorage.setItem('noderadar_last_ble_device_id', this.device.id);
        } catch {}
      }

      await this.setupGattConnection(this.device);
    } catch (err) {
      console.error('[BleTransport] Connection failed:', err);
      await this.disconnect();
      throw err;
    }
  }

  /**
   * Reconnects to the previously paired Bluetooth device without popping the user device picker dialog.
   *
   * Why: Allows background reconnection with exponential backoff when a Bluetooth link drops while mobile,
   * avoiding hazardous modal prompts and maintaining hands-free operational continuity.
   */
  async reconnect(): Promise<void> {
    if (this.isConnected()) return;

    if (!('bluetooth' in navigator)) {
      throw new Error('WebBluetooth API is not supported by this browser.');
    }

    const nav = navigator as NavigatorWithBluetooth;
    let targetDevice: BleDeviceLike | null = this.device || this.lastDevice;

    // If device reference was released, attempt retrieval from granted devices
    if (!targetDevice && typeof nav.bluetooth?.getDevices === 'function') {
      try {
        const devices: BleDeviceLike[] = await nav.bluetooth.getDevices();
        const storedId = typeof localStorage !== 'undefined'
          ? localStorage.getItem('noderadar_last_ble_device_id')
          : null;
        targetDevice = (storedId ? devices.find((d) => d.id === storedId) : null) || devices[0] || null;
      } catch (err) {
        console.warn('[BleTransport] navigator.bluetooth.getDevices lookup failed:', err);
      }
    }

    if (!targetDevice) {
      throw new Error('No previously authorized Bluetooth device found for background reconnect.');
    }

    this.device = targetDevice;
    this.lastDevice = targetDevice;

    try {
      console.info('[BleTransport] Reconnecting to existing BLE device:', targetDevice.name || targetDevice.id);
      await this.setupGattConnection(targetDevice);
    } catch (err) {
      console.warn('[BleTransport] Background reconnect attempt failed:', err);
      throw err;
    }
  }

  /**
   * Establishes GATT server connection, retrieves characteristics, and attaches notification listeners.
   */
  private async setupGattConnection(device: BleDeviceLike): Promise<void> {
    if (this.boundDisconnectHandler && this.device?.removeEventListener) {
      this.device.removeEventListener('gattserverdisconnected', this.boundDisconnectHandler);
    }

    this.boundDisconnectHandler = () => {
      console.warn('[BleTransport] GATT server disconnected event fired');
      this.handleDisconnect();
    };

    if (device.addEventListener) {
      device.addEventListener('gattserverdisconnected', this.boundDisconnectHandler);
    }

    console.info('[BleTransport] Connecting to GATT server...');
    if (!device.gatt) {
      throw new Error('Device does not have a GATT server available.');
    }
    this.server = await device.gatt.connect();
    this.service = await this.server.getPrimaryService(MESHTASTIC_SERVICE_UUID);

    // Obtain characteristics
    this.fromRadioChar = await this.service.getCharacteristic(FROMRADIO_CHAR_UUID);
    this.toRadioChar = await this.service.getCharacteristic(TORADIO_CHAR_UUID);

    console.info('[BleTransport] GATT characteristics obtained (FromRadio, ToRadio)');

    // Subscribe to FROMNUM notifications (official signal indicating pending FromRadio packets)
    try {
      this.fromNumChar = await this.service.getCharacteristic(FROMNUM_CHAR_UUID);
      await this.enqueueGatt(() => this.fromNumChar!.startNotifications());
      this.fromNumChar.addEventListener('characteristicvaluechanged', () => {
        console.debug('[BleTransport] FROMNUM notification received, draining FROMRADIO...');
        this.drainFromRadio();
      });
      console.info('[BleTransport] Subscribed to FROMNUM notifications');
    } catch (err) {
      console.warn('[BleTransport] FROMNUM characteristic unavailable or subscription failed:', err);
    }

    // Check if fromRadioChar also supports direct notifications (some firmware variants push here)
    if (this.fromRadioChar.properties && this.fromRadioChar.properties.notify) {
      try {
        await this.enqueueGatt(() => this.fromRadioChar!.startNotifications());
        this.fromRadioChar.addEventListener('characteristicvaluechanged', (event: Event) => {
          const target = event.target as { value?: DataView } | null;
          const value = target?.value;
          if (value && value.byteLength > 0) {
            const uint8 = new Uint8Array(value.buffer, value.byteOffset, value.byteLength);
            this.emitPacket(uint8);
          }
        });
        console.info('[BleTransport] Subscribed to FROMRADIO direct notifications');
      } catch {
        // Direct FROMRADIO notification is optional; drain protocol remains primary
      }
    }

    this.isConnectedState = true;
    console.info('[BleTransport] Connected successfully');

    // Periodic drain fallback (every 500ms) to counteract Android notification throttling
    if (this.pollTimer !== null) {
      clearInterval(this.pollTimer);
    }
    this.pollTimer = setInterval(() => {
      if (this.isConnectedState && !this.isDraining) {
        this.drainFromRadio();
      }
    }, 500);

    // Initial drain: flush any stale data in FROMRADIO buffer from prior sessions
    await this.drainFromRadio();
  }

  /**
   * Disconnects GATT server and cleans up resources.
   *
   * Why: Releases GATT subscriptions, clears recurring safety timers, and cleans device handles
   * to allow clean reconnection without orphaned Bluetooth stack state.
   */
  async disconnect(): Promise<void> {
    this.isConnectedState = false;
    this.isDraining = false;
    this.needsRedrain = false;

    if (this.pollTimer !== null) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }

    if (this.fromNumChar) {
      try {
        await this.fromNumChar.stopNotifications();
      } catch {
        // Notification cleanup
      }
      this.fromNumChar = null;
    }

    if (this.fromRadioChar) {
      try {
        await this.fromRadioChar.stopNotifications();
      } catch {
        // Notification cleanup
      }
      this.fromRadioChar = null;
    }

    if (this.server && this.server.connected) {
      try {
        this.server.disconnect();
      } catch {
        // Server disconnect cleanup
      }
      this.server = null;
    }

    this.device = null;
    this.service = null;
    this.toRadioChar = null;
    this.gattQueue = Promise.resolve();
  }

  /**
   * Transmits raw ToRadio Protobuf bytes over the BLE TORADIO characteristic.
   *
   * Why: Meshtastic firmware declares CHR_PROPS_WRITE (acknowledged) on TORADIO.
   * However, depending on ESP32 NimBLE vs nRF52 SoftDevice configuration, writeValueWithResponse
   * or writeValueWithoutResponse may be enforced. This method inspects characteristic properties
   * and provides fallback execution through the serialized GATT queue.
   *
   * @param payload - Protobuf serialized payload
   */
  async send(payload: Uint8Array): Promise<void> {
    if (!this.toRadioChar || !this.isConnectedState) {
      throw new Error('Bluetooth device is not connected.');
    }

    console.debug(`[BleTransport] Sending ${payload.length} bytes to ToRadio`);

    const toChar = this.toRadioChar;
    await this.enqueueGatt(async () => {
      const props = toChar.properties || {};

      if (props.write && toChar.writeValueWithResponse) {
        try {
          await toChar.writeValueWithResponse(payload);
          return;
        } catch (err) {
          console.warn('[BleTransport] writeValueWithResponse failed, attempting fallback without response:', err);
        }
      }

      if (toChar.writeValueWithoutResponse) {
        try {
          await toChar.writeValueWithoutResponse(payload);
          return;
        } catch (err) {
          console.warn('[BleTransport] writeValueWithoutResponse failed:', err);
        }
      }

      if (toChar.writeValueWithResponse) {
        await toChar.writeValueWithResponse(payload);
      } else if (toChar.writeValue) {
        await toChar.writeValue(payload);
      }
    });

    // Poke drain immediately after transmission to capture quick replies from the radio
    this.drainFromRadio();
  }

  /**
   * Registers a packet callback invoked whenever a valid FromRadio message is drained.
   *
   * Why: Bridges incoming low-level BLE characteristic buffers to the upper protocol codec.
   */
  onPacket(handler: (data: Uint8Array) => void): void {
    this.packetHandlers.push(handler);
  }

  /**
   * Registers an error handler for transport failures.
   *
   * Why: Surfaces unhandled transport exceptions to supervisor logic.
   */
  onError(handler: (error: Error) => void): void {
    this.errorHandlers.push(handler);
  }

  /**
   * Registers a disconnect handler for unexpected BLE link drop events.
   *
   * Why: Enables automatic reconnection supervisors to trigger re-association.
   */
  onDisconnect(handler: () => void): void {
    this.disconnectHandlers.push(handler);
  }

  /**
   * Checks whether the GATT server connection is currently active.
   *
   * Why: Prevents attempting I/O operations against an unlinked transport.
   */
  isConnected(): boolean {
    return this.isConnectedState && !!this.server && this.server.connected;
  }

  /**
   * Drains all pending packets from FROMRADIO characteristic.
   *
   * Why: The Meshtastic BLE specification mandates that clients repeatedly read
   * FROMRADIO until a 0-byte (empty) response indicates that the radio FIFO queue
   * is exhausted. Because Android GATT is sensitive to timing, this method:
   * 1. Implements the canonical needsRedrain state machine to avoid dropped notifications.
   * 2. Yields 15ms between consecutive reads to prevent Android GATT buffer corruption.
   * 3. Retries briefly when an empty response arrives if a redrain was flagged.
   */
  private async drainFromRadio(): Promise<void> {
    if (!this.fromRadioChar || !this.isConnectedState) return;

    if (this.isDraining) {
      this.needsRedrain = true;
      return;
    }

    this.isDraining = true;

    try {
      while (this.isConnectedState && this.fromRadioChar) {
        this.needsRedrain = false;
        let readCount = 0;

        while (this.isConnectedState && this.fromRadioChar) {
          const fromChar = this.fromRadioChar;
          if (!fromChar || !fromChar.readValue) {
            break;
          }

          let dataView: DataView | null = null;
          try {
            dataView = await this.enqueueGatt(() => fromChar.readValue!());
          } catch (readErr) {
            if (this.isConnectedState) {
              console.warn('[BleTransport] FROMRADIO readValue failed:', readErr);
            }
            break;
          }

          if (!dataView || dataView.byteLength === 0) {
            break;
          }

          const uint8 = new Uint8Array(dataView.buffer, dataView.byteOffset, dataView.byteLength);
          readCount++;
          console.debug(`[BleTransport] FROMRADIO read #${readCount}: ${uint8.length} bytes`);
          this.emitPacket(uint8);

          // Yield 15ms so the device firmware and Android GATT stack can turnaround
          await new Promise((r) => setTimeout(r, 15));
        }

        // If another notification arrived during the inner loop, perform another drain pass
        if (!this.needsRedrain) {
          break;
        }
      }
    } catch (err) {
      if (this.isConnectedState) {
        console.warn('[BleTransport] Error during FROMRADIO drain:', err);
      }
    } finally {
      this.isDraining = false;
    }
  }

  private emitPacket(data: Uint8Array): void {
    for (const handler of this.packetHandlers) {
      try {
        handler(data);
      } catch (err) {
        console.error('[BleTransport] Packet handler error:', err);
      }
    }
  }

  private handleDisconnect(): void {
    const wasConnected = this.isConnectedState;
    this.isConnectedState = false;

    if (wasConnected) {
      for (const handler of this.disconnectHandlers) {
        try {
          handler();
        } catch (err) {
          console.error('[BleTransport] Disconnect handler error:', err);
        }
      }
    }
  }
}
